<template>
  <article class="clients split-view">
    <component :is="modalComponent" :displayed="modalDisplay" @close="closeModal" />

    <div v-if="isLoadingClients" class="spinner-section">
      <div class="spinner-container">
        <loading-spinner class="spinner" />
        <span v-t>Loading your clients<three-loading-dots /></span>
      </div>
    </div>

    <div v-else-if="!hasClients" class="client-list-area no-client">
      <div v-if="!hasProfile" class="onboard">
        <img src="~@imagesBrand/tada.png" :alt="$t('no clients')">
        <h4 v-t>Welcome!</h4>
        <p><em v-t>To start inviting your clients, create a profile.</em></p>
        <button v-t type="button" @click="showModal('profile')">Create Profile</button>
      </div>

      <div v-if="hasProfile">
        <img src="~@imagesBrand/no-clients.png" :alt="$t('no clients')">
        <h4 v-t>You don't have any clients, yet.</h4>
        <p><em v-t>They're out there! You just need to invite them.</em></p>
        <button v-t type="button" @click="showClientInvitationModal()">Invite Client</button>
      </div>
    </div>

    <span v-else-if="hasClients" class="client-list-area">
      <header class="title">
        <h4 v-t>Clients</h4>
        <button v-t type="button" class="desktop-invite-button" @click="showClientInvitationModal()">Invite Client</button>
      </header>

      <div class="search-field-container">
        <input v-model="searchField" type="search" class="search-input" :placeholder="$t('Search client names')">
      </div>

      <div v-if="searchField !== '' && clientsFound.length === 0" class="no-client-found">
        <div>
          <svgicon name="search" :alt="$t('search')" height="1.1rem" width="1.1rem" />
          <span v-t>No client names matched your search for</span>
          &ldquo;{{ searchField }}&rdquo;
        </div>
      </div>

      <list v-else-if="searchField !== ''" :key="searchField" :list-items="clientsFound">
        <header slot="header-labels" class="header-labels">
          <small v-t class="strong">Name</small>
          <span>
            <small>{{ clientsFound.length }}&nbsp;</small>
            <small v-t>Result</small>
            <small>{{ clientsFound.length > 1 ? 's' : '' }}</small>
          </span>
        </header>

        <li slot="listItem" slot-scope="props" :class="{ selected: isClientSelected(props) }">
          <router-link :to="'/app/clients/' + props.value.id" class="data">
            <span>{{ props.value.firstName }}</span><span>{{ props.value.lastName }}</span>
          </router-link>
        </li>
      </list>

      <list v-else button-size="small" :order-by="orderBy()" :list-items="allClients" @optionSelected="optionSelected($event)">
        <header :key="props.order.sortBy" slot="header-labels" slot-scope="props" class="header-labels">
          <small v-t class="strong">Name</small>
          <small v-if="props.order.sortBy !== 'invitedAt'" v-t class="strong">Assets</small>
          <small v-else v-t class="strong">Date Invited</small>
        </header>

        <li slot="listItem" slot-scope="props" :class="{ selected: isClientSelected(props) }">
          <router-link :to="'/app/clients/' + props.value.id" class="data">
            <div>
              <div v-if="props.value.firstName || props.value.lastName">
                <span v-if="props.value.firstName">{{ props.value.firstName }}</span>
                <span v-if="props.value.lastName">{{ props.value.lastName }}</span>
              </div>
              <span v-else>{{ props.value.email }}</span>
            </div>
            <div>
              <span v-if="props.value.declinedAt != null" v-t>Declined</span>
              <span v-else-if="isSortedByInvitedAt">{{ props.value.invitedAt | formatDate }}</span>
              <div v-else>
                <span v-show="isClientPending(props.value)" v-t>Invited</span>
                <span v-show="!isClientPending(props.value)" class="tab-numbers">{{ props.value.assets | formatCurrencyRounded }}</span>
              </div>
            </div>
          </router-link>
        </li>
      </list>
    </span>

    <button v-if="hasClients" type="button" class="large icon circle add-client" @click="showClientInvitationModal()">
      <svg viewBox="0 0 32 32">
        <path d="M16,16c-3.9,0-7-3.1-7-7s3.1-7,7-7s7,3.1,7,7S19.9,16,16,16z M16,4c-2.8,0-5,2.2-5,5s2.2,5,5,5s5-2.2,5-5S18.8,4,16,4z" />
        <path d="M22,28H5v-3.1c0-1.4,0.8-2.7,2.1-3.3c4.6-2.3,10.1-2.7,14.9-1.2v-2.1c-5.2-1.4-11-1-15.8,1.5c-2,1-3.2,2.9-3.2,5.1V30h19V28 z" />
        <polygon points="32,23 27,23 27,18 25,18 25,23 20,23 20,25 25,25 25,30 27,30 27,25 32,25 " />
      </svg>
    </button>

    <aside class="report-section">
      <overview />
      <revenue />
      <resources />
    </aside>

    <router-view />
  </article>
</template>

<script>
import { mapGetters, mapGetterMethods, mapActions } from '@/lib/vue';
import {
  banner, list, loadingSpinner, threeLoadingDots,
} from '@/components/layout';
import {
  revenue, overview, clientInviteModal, profile, unverifiedEmailModal, resources,
} from '@/components/advisor';
import filters from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import { isClientPending } from '@/store/logic';
import '@/lib/icons/add-user';
import errorStore from '@/store/error';

const _ = { errorStore };

const brandConfig = getBrandConfig();

export default {
  _,
  head() {
    const title = this.$t('Clients');
    return {
      title: `${title} | ${brandConfig.fullName}`,
    };
  },

  components: {
    banner, clientInviteModal, profile, list, revenue, overview, loadingSpinner, threeLoadingDots, unverifiedEmailModal, resources,
  },

  filters,

  data() {
    return {
      searchField: '',
      modalDisplay: false,
      modalComponent: '',
      selectedSortOption: 'firstName',
    };
  },

  computed: {
    ...mapGetters(['allClients', 'hasClients', 'account', 'hasProfile', 'isLoadingClients', 'isEmailVerified']),
    isSortedByInvitedAt: {
      get() { return this.selectedSortOption === 'invitedAt'; },
      set(newVal) { this.selectedSortOption = newVal; },
    },
    clientsFound() {
      return this.searchClients(this.searchField);
    },
  },

  async created() {
    try {
      await Promise.all([
        this.fetchClients(),
        this.fetchEmailVerified(),
      ]);
    } catch (exception) {
      _.errorStore.addError();
    }
  },

  methods: {
    ...mapGetterMethods(['searchClients']),

    ...mapActions(['fetchClients', 'fetchEmailVerified']),

    isClientPending,

    isClientSelected(props) {
      return (new RegExp('clients/:id/index')).test(this.$route.name)
          && Number(this.$route.params.id) === props.value.id;
    },

    showModal(comp) {
      this.modalComponent = comp;
      this.modalDisplay = true;
    },

    // this method seems intricated but I use this system to only call the server when it is absolutely necessary
    async showClientInvitationModal() {
      if (this.isEmailVerified) {
        this.showModal('client-invite-modal');
      } else {
        await this.fetchEmailVerified();
        if (this.isEmailStillVerified()) {
          this.showModal('client-invite-modal');
        } else {
          this.showModal('unverified-email-modal');
        }
      }
    },

    // Workaround for testing where I can not change the value of a computed property in the middle of a test
    isEmailStillVerified() {
      return this.isEmailVerified;
    },

    closeModal() {
      this.modalDisplay = false;
    },

    optionSelected(order) {
      this.selectedSortOption = order.sortBy;
    },

    orderBy() {
      return [
        {
          orderLabel: this.$t('Sort by'), label: this.$t('First Name'), sortBy: 'firstName', asc: true, dataType: 'string',
        },
        {
          orderLabel: this.$t('Sort by'), label: this.$t('Last Name'), sortBy: 'lastName', asc: true, dataType: 'string',
        },
        {
          orderLabel: this.$t('Sort by'), label: this.$t('Assets'), sortBy: 'assets', asc: false,
        },
        {
          orderLabel: this.$t('Filter by'), label: this.$t('Pending Invitations'), sortBy: 'invitedAt', asc: true, filter: isClientPending,
        },
      ];
    },
  },
};
</script>

<style scoped lang="scss">


  .client-list-area {
    flex-flow: column wrap;
    flex: 1 1 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    .search-field-container {
      margin: 1rem;
      @media #{$screen-width-medium} { margin: 0 1rem; }
    }

    .no-client-found {
      height: 65%;
      display: flex;
      width: 100%;
      align-items: center;

      > * {
        width: 85%;
        margin: 0 auto;
        text-align: center;
        color: $pearl-500;

        span {
          display: block;
          word-wrap: break-word;
        }
      }

      @media #{$screen-width-medium} {
        height: 45%;

        > * {
          width: 30%;
        }
      }
    }
  }

  .no-client {
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      text-align: center;
      max-width: 90%;
      margin-top: -2 * 4rem;
    }

    img {
      width: 4rem;
      margin: 1rem 0;
    }

    p {
      margin: 0.5rem 0 1rem;
    }

    .no-client-invite-button {
      display: none;
      margin: 1rem auto;
    }

    @media #{$screen-width-medium} {
      > div {
        margin-top: -3rem;
      }

      .no-client-invite-button {
        display: block;
      }
    }
  }

  .title {
    display: none;
    margin: 0 1rem;
    @media #{$screen-width-medium} {
      display: flex;
    }
  }

  .header-labels {
    display: flex;
    justify-content: space-between;
    margin: 1rem;
  }

  .header-labels > span {
    color: $pearl-700;
  }

  .data {
    span:first-child { padding-right: 0.25rem; }
    span:nth-child(2) { flex-grow: 1; }
  }

  .add-client {
    position: fixed;
    bottom: 1rem;
    right: 1rem;

    @media #{$screen-width-medium} {
      display: none;
    }
  }

  .report-section {
    padding: 0 1rem;
    display: none;
    @media #{$screen-width-medium} {
      display: block;
      width: 26rem;
      position: relative;
      right: 0;
    }
  }

  .spinner-section {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
  }
</style>
